import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import axios from 'axios';
import React, { useState, useEffect, useMemo } from 'react';
import { Form, Row, Col, Spinner } from 'react-bootstrap';

// Define the interface for each row in procRows
interface ProcRow {
  id: number;
  time: string;
  descripcion: string;
  proc: string;
  caratula: string;
  usuario: string;
}

// Define the interface for props
interface MovimientosDashboardTableProps {
  procRows: ProcRow[];
}

// Utility function to truncate text
const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return `${text.substring(0, maxLength)}...`;
  }
  return text;
};

const columns: ColumnDef<ProcRow>[] = [
  {
    header: 'Hora',
    accessorKey: 'time',
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-body white-space-nowrap' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' }
    }
  },
  {
    accessorKey: 'usuario',
    header: 'Responsable',
    cell: ({ row: { original } }) => {
      const [iniciales, setIniciales] = useState<string[]>([]);
      const [loading, setLoading] = useState<boolean>(true);
      const [error, setError] = useState<string | null>(null);
      const [isEditing, setIsEditing] = useState<boolean>(false);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';

      // Fetch Iniciales from miem table
      useEffect(() => {
        const fetchIniciales = async () => {
          try {
            const response = await axios.get(`${apiUrl}/miembros/iniciales`);
            setIniciales(response.data);
            setLoading(false);
          } catch (err) {
            console.error('Error fetching Iniciales:', err);
            setError('Failed to load usuarios');
            setLoading(false);
          }
        };
        fetchIniciales();
      }, []);

      // Handle click to show dropdown
      const handleTextClick = () => {
        setIsEditing(true);
      };

      const handleBlur = () => {
        setIsEditing(false);
      };

      // Handle usuario change
      const handleUsuarioChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newUsuario = e.target.value;
        try {
          await axios.post(`${apiUrl}/mtos/update-usuario`, {
            id: original.id,
            usuario: newUsuario,
          });
          // Update the local row data to reflect the change
          original.usuario = newUsuario;
        } catch (err) {
          console.error('Error updating usuario:', err);
          alert('Failed to update usuario');
        } finally {
            setIsEditing(false);
        }
      };

      if (loading) return <span><Spinner animation="border" size="sm" /></span>;
      if (error) return <span>{error}</span>;
      if (!Array.isArray(iniciales)) return <span>Error: Invalid usuarios data</span>;

      return isEditing ? (
        <select
          value={original.usuario || ''}
          onChange={handleUsuarioChange}
          onBlur={handleBlur} 
          className="form-select form-select-sm"
          style={{ width: '100px' }}
          autoFocus
        >
          <option value="">Select...</option>
          {iniciales.map((inicial) => (
            <option key={inicial} value={inicial}>
              {inicial}
            </option>
          ))}
        </select>
      ) : (
        <span
          onClick={handleTextClick}
          style={{ cursor: 'pointer' }}
          title="Click to edit"
        >
          {original.usuario || 'N/A'}
        </span>
      );
    },
    meta: {
      cellProps: { className: 'ps-3 white-space-nowrap' },
      headerProps: { style: { width: '10%' }, className: 'ps-3' },
    },
  },
  {
    accessorKey: 'proc',
    header: 'Proceso',
    cell: ({ row: { original } }) => {
      const truncatedCaratula = truncateText(original.caratula, 30);
      return (
        <Link
          to={`/app/apps/project-management/proceso-details/${original.proc}`}
          className="text-decoration-none fw-bold fs-8 text-truncate"
          title={original.caratula}
        >
          {truncatedCaratula}
        </Link>
      );
    },
    meta: {
      cellProps: { 
        className: 'white-space-nowrap text-truncate',
        style: { maxWidth: '200px' } 
      },
      headerProps: { style: { width: '30%' } }
    }
  },
  {
    accessorKey: 'caratula',
    header: 'Movimiento',
    cell: ({ row: { original } }) => {
      const truncatedCaratula = truncateText(original.descripcion, 50); 
      return (
        <Link
          to={`/app/apps/project-management/movimiento/${original.id}`}
          className="text-decoration-none fw-bold fs-8 text-truncate"
          title={original.descripcion}
        >
          {truncatedCaratula}
        </Link>
      );
    },
    meta: {
      cellProps: { 
        className: 'white-space-nowrap text-truncate',
        style: { maxWidth: '300px' } 
      },
      headerProps: { style: { width: '35%' } }
    }
  },
  {
    header: 'Descripción',
    accessorKey: 'descripcion',
    cell: ({ row: { original } }) => {
      return <span>{original.descripcion}</span>;
    },
    meta: {
      cellProps: { className: 'ps-3 fs-9 text-body white-space-nowrap' },
      headerProps: { style: { width: '15%' }, className: 'ps-3' }
    }
  }
];

const MovimientosDashboardTable: React.FC<MovimientosDashboardTableProps> = ({ procRows }) => {
  // 1. Estados para los filtros y ordenamiento
  const [selectedMiembro, setSelectedMiembro] = useState<string>('');
  const [sortBy, setSortBy] = useState<'proceso' | 'hora'>('proceso');
  
  // 2. Extraer lista única de miembros
  const uniqueMiembros = useMemo(() => {
    const miembrosSet = new Set(procRows.map(row => row.usuario).filter(u => u));
    return Array.from(miembrosSet).sort();
  }, [procRows]);

  // 3. Filtrar y luego ordenar las filas
  const processedRows = useMemo(() => {
    let result = [...procRows];

    // Primero filtramos por miembro si hay uno seleccionado
    if (selectedMiembro) {
      result = result.filter(row => row.usuario === selectedMiembro);
    }

    // Luego ordenamos el resultado según el criterio seleccionado
    if (sortBy === 'hora') {
      result.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    } else if (sortBy === 'proceso') {
      result.sort((a, b) => (a.caratula || '').localeCompare(b.caratula || ''));
    }

    return result;
  }, [procRows, selectedMiembro, sortBy]);

  // 4. Inicializar la tabla con las filas procesadas
  const table = useAdvanceTable({
    data: processedRows,
    columns,
    pageSize: 150,
    pagination: true,
    sortable: true
  });

  return (
    <div style={{ width: '100%' }}>
      {/* SECCIÓN DE FILTROS Y ORDENAMIENTO */}
      <Row className="mb-4 justify-content-end gy-2">
        <Col xs="auto">
          <Form.Group className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 fw-bold text-nowrap text-body-tertiary">
              Ordenar por:
            </Form.Label>
            <Form.Select 
              size="sm"
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value as 'proceso' | 'hora')}
              style={{ minWidth: '130px' }}
            >
              <option value="proceso">Proceso (A-Z)</option>
              <option value="hora">Hora</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs="auto">
          <Form.Group className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 fw-bold text-nowrap text-body-tertiary">
              Responsable:
            </Form.Label>
            <Form.Select 
              size="sm"
              value={selectedMiembro} 
              onChange={(e) => setSelectedMiembro(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">Todos</option>
              {uniqueMiembros.map(miembro => (
                <option key={miembro} value={miembro}>
                  {miembro}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* TABLA */}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            tableProps={{
              className: 'phoenix-table border-top border-translucent fs-9 table-responsive',
              style: { width: '100%', tableLayout: 'fixed' }
            }}
          />
          <AdvanceTableFooter pagination />
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default MovimientosDashboardTable;